// /client/src/pages/Admin/DashboardAdherents/LatestMembers.jsx
import React, { useEffect, useState } from "react";
import { getLatestMembers } from "../../../services/dashboardServices";

/* Couleurs statut (cohérentes avec le reste de l’app) */
const statusBadge = (statut) => {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-900";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-900";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-rose-300 text-slate-900";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
};

const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

const fallbackPhoto = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

function MemberCard({ a }) {
  const photo =
    a?.photo_url && a.photo_url !== "/img/default_avatar.png"
      ? a.photo_url
      : fallbackPhoto(a?.genre);

  const groups = Array.isArray(a?.Groups)
    ? a.Groups.map((g) => g?.nom).filter(Boolean)
    : [];

  return (
    <div className="group rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur shadow-sm hover:shadow-md transition">
      {/* Image + overlays */}
      <div className="relative h-36 w-full overflow-hidden">
        <img
          src={toAbsolute(photo)}
          alt={`${a?.prenom || ""} ${a?.nom || ""}`}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          loading="lazy"
        />

        {/* Groupes (tag) */}
        {groups.length > 0 && (
          <div className="absolute left-2 bottom-2 max-w-[75%]">
            <span className="inline-flex items-center rounded-md bg-black/60 text-white px-2 py-0.5 text-[11px] backdrop-blur-sm">
              <span className="truncate">{groups.join(", ")}</span>
            </span>
          </div>
        )}

        {/* Statut (badge) */}
        {a?.statut_inscription && (
          <div className="absolute right-2 top-2">
            <span
              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1 ring-white/30 ${statusBadge(
                a.statut_inscription
              )}`}
              title="Statut d'inscription"
            >
              {a.statut_inscription}
            </span>
          </div>
        )}
      </div>

      {/* Infos */}
      <div className="p-3">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
          {(a?.nom || "").toUpperCase()} {a?.prenom || ""}
        </div>
        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 flex items-center justify-between">
          <span className="shrink-0" title="Créé le">
            {fmtDateTime(a?.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur shadow-sm">
      <div className="h-36 bg-slate-200 dark:bg-slate-800 animate-pulse" />
      <div className="p-3 space-y-2">
        <div className="h-4 w-3/5 bg-slate-200 dark:bg-slate-800 animate-pulse rounded" />
        <div className="h-3 w-4/5 bg-slate-200 dark:bg-slate-800 animate-pulse rounded" />
      </div>
    </div>
  );
}

export default function LatestMembers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const members = await getLatestMembers();
        if (!alive) return;
        const normalized =
          (Array.isArray(members) ? members : []).map((m) => ({
            ...m,
            Groups: Array.isArray(m?.Groups)
              ? m.Groups.map((g) => ({ nom: g?.nom })).filter(Boolean)
              : [],
          })) || [];
        setItems(normalized.slice(0, 10)); // max 10
      } catch (e) {
        console.error("Erreur lors du chargement des derniers adhérents :", e);
        setItems([]);
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête simple + compteur */}
      <div className="px-5 pt-5 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
              Derniers adhérents inscrits
            </h3>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
              {loading ? "Chargement…" : `${items.length} affiché(s)`}
            </div>
          </div>
          <div className="h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
        </div>
      </div>

      <div className="px-5 pb-5 sm:px-6 sm:pb-6">
        {/* Mobile : carrousel horizontal swipe */}
        <div className="md:hidden -mx-2 px-2 overflow-x-auto snap-x snap-mandatory">
          <div className="grid grid-flow-col auto-cols-[minmax(200px,1fr)] gap-3 snap-x">
            {loading
              ? [...Array(5)].map((_, i) => <SkeletonCard key={i} />)
              : items.map((a) => (
                  <div key={a.id} className="snap-start">
                    <MemberCard a={a} />
                  </div>
                ))}
          </div>
        </div>

        {/* Desktop : grille 5 colonnes (2 lignes max => 10 items) */}
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-4">
          {loading
            ? [...Array(10)].map((_, i) => <SkeletonCard key={i} />)
            : items.map((a) => <MemberCard key={a.id} a={a} />)}
        </div>
      </div>
    </section>
  );
}
